// A Smartsheet-style multi-select dropdown cell ("Dropdown (multi select)" columns such as GRP's Layout and
// Flooring). The cell shows the chosen values as chips; a click opens the column's whole option list with a
// tick box per option, as Smartsheet does, and the choice is saved when the list closes (click away, Escape
// or Done), not on every tick. Desktop: a popover under the cell. Touch: a bottom sheet.
// A column without options keeps free-text tags (type one, Enter).
import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent, type MouseEvent } from 'react';
import { createPortal } from 'react-dom';
import { useIsCoarse } from '../lib/media.js';
import { fold, hasText } from '../lib/search.js';
import { Modal } from './ui.js';

const stop = (e: MouseEvent) => e.stopPropagation();
const POP_W = 240;
const POP_MAX_H = 320;

/** the chosen values in the column's option order, then any value the list doesn't have (kept, never lost). */
export function orderPicked(picked: readonly string[], options: readonly string[]): string[] {
  const set = new Set(picked);
  return [...options.filter((o) => set.has(o)), ...picked.filter((p) => !options.includes(p))];
}

export function MultiPick({ label, value, options, onChange }: {
  label: string;
  value: string[];
  options: string[];
  onChange: (next: string[]) => void;
}) {
  const coarse = useIsCoarse();
  const anchor = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<string[]>(value);
  const [q, setQ] = useState('');
  const [text, setText] = useState('');
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const pop = useRef<HTMLDivElement>(null);

  // every option, then any saved value the column's list doesn't have
  const all = [...options, ...value.filter((v) => !options.includes(v))];
  const shown = q.trim() ? all.filter((o) => hasText(o, q)) : all;

  const openList = () => { setDraft(value); setQ(''); setText(''); setOpen(true); };
  const close = (save: boolean) => {
    setOpen(false);
    const next = orderPicked(draft, options);
    const was = orderPicked(value, options);
    const same = next.length === was.length && next.every((v, i) => v === was[i]);
    if (save && !same) onChange(next);
    anchor.current?.focus();
  };
  const toggle = (o: string) => setDraft((d) => (d.includes(o) ? d.filter((x) => x !== o) : [...d, o]));
  const addText = () => {
    const t = text.trim();
    setText('');
    if (t && !draft.some((d) => fold(d) === fold(t))) setDraft((d) => [...d, t]);
  };

  // desktop: place the list under the cell (above it when there's no room), inside the window
  useLayoutEffect(() => {
    if (!open || coarse || !anchor.current) return;
    const r = anchor.current.getBoundingClientRect();
    const below = window.innerHeight - r.bottom;
    const top = below >= Math.min(POP_MAX_H, 200) + 8 ? r.bottom + 4 : Math.max(8, r.top - 4 - POP_MAX_H);
    setPos({ top, left: Math.max(8, Math.min(r.left, window.innerWidth - POP_W - 8)) });
  }, [open, coarse]);
  // ...and close it (saving) on a click elsewhere or when the page scrolls under it
  useEffect(() => {
    if (!open || coarse) return;
    const onDown = (e: Event) => {
      const t = e.target as Node;
      if (!pop.current?.contains(t) && !anchor.current?.contains(t)) close(true);
    };
    const onScroll = (e: Event) => { if (!pop.current?.contains(e.target as Node)) close(true); };
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('scroll', onScroll, true);
    };
  });

  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(false); }
  };

  const list = (
    <>
      {all.length > 8 && (
        <input className="field mpick-q" autoFocus value={q} placeholder="Search" aria-label={`Search ${label}`} onChange={(e) => setQ(e.currentTarget.value)} />
      )}
      <ul className="mpick-list" role="group" aria-label={label}>
        {shown.map((o) => (
          <li key={o}>
            <label className="mpick-opt">
              <input type="checkbox" checked={draft.includes(o)} onChange={() => toggle(o)} autoFocus={all.length <= 8 && o === all[0]} />
              <span>{o}</span>
            </label>
          </li>
        ))}
        {/* tags typed here earlier (a column without options), shown so they can be unticked */}
        {!options.length && draft.filter((d) => !all.includes(d)).map((o) => (
          <li key={o}>
            <label className="mpick-opt"><input type="checkbox" checked onChange={() => toggle(o)} /><span>{o}</span></label>
          </li>
        ))}
        {shown.length === 0 && options.length > 0 && <li className="mpick-none">No match</li>}
      </ul>
      {!options.length && (
        <input
          className="field mpick-q" autoFocus value={text} placeholder="Type a value, then Enter" aria-label={`Add ${label}`}
          onChange={(e) => setText(e.currentTarget.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addText(); } }}
        />
      )}
    </>
  );

  return (
    <>
      <button
        ref={anchor}
        type="button"
        className={`mpick${open ? ' is-open' : ''}`}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={`${label}: ${value.length ? value.join(', ') : 'none'}. Change`}
        onClick={(e) => { stop(e); if (open) close(true); else openList(); }}
      >
        {value.length ? (
          <span className="grp-chips">{value.map((t) => <span key={t} className="grp-chip">{t}</span>)}</span>
        ) : <span className="mpick-empty" />}
        <span className="mpick-caret" aria-hidden="true">▾</span>
      </button>
      {/* portalled: a cell inside a sticky or transformed grid would trap the sheet's fixed layer */}
      {open && coarse && createPortal(
        <Modal
          sheet
          title={label}
          onClose={() => close(true)}
          footer={(
            <>
              <button type="button" className="btn" onClick={() => setDraft([])}>Clear</button>
              <button type="button" className="btn btn-primary" onClick={() => close(true)}>Done</button>
            </>
          )}
        >
          <div className="mpick-sheet" onKeyDown={onKey}>{list}</div>
        </Modal>,
        document.body,
      )}
      {open && !coarse && pos && createPortal(
        <div ref={pop} className="mpick-pop" style={{ top: pos.top, left: pos.left, width: POP_W, maxHeight: POP_MAX_H }} onKeyDown={onKey} onClick={stop}>
          {list}
          <div className="mpick-foot">
            <button type="button" className="btn btn-sm" onClick={() => setDraft([])} disabled={!draft.length}>Clear</button>
            <button type="button" className="btn btn-sm btn-primary" onClick={() => close(true)}>Done</button>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
